import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import API from '../api';
import Modal from './Modal';
import RichTextEditor from '../components/RichTextEditor';
import { slugify, sanitizeSlugInput } from '../utils/slug';

const CATEGORIES = [
  { value: 'highlight', label: 'Highlight' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'event', label: 'Event' },
];

const EMPTY = {
  title: '', slug: '', category: 'announcement', excerpt: '', content: '', image_url: '',
  published_at: new Date().toISOString().slice(0, 10), sort_order: 0, is_active: 1,
};
const BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';

function formatDate(str) {
  return str ? new Date(str).toLocaleDateString('en-MY', { dateStyle: 'medium' }) : '—';
}

export default function AdminPromotions() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => API.get('/promotions/all').then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    const nextOrder = items.length ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;
    setForm({ ...EMPTY, sort_order: nextOrder });
    setModal({ mode: 'add' });
  };
  const openEdit = (item) => {
    setForm({ ...item, published_at: item.published_at ? item.published_at.slice(0, 10) : '' });
    setModal({ mode: 'edit', id: item.id });
  };

  const handleTitleChange = (title) => {
    setForm(f => ({ ...f, title, slug: modal?.mode === 'add' ? slugify(title) : f.slug }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: data.url }));
    } catch { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, published_at: form.published_at ? new Date(form.published_at).toISOString() : null };
      if (modal.mode === 'add') await API.post('/promotions', payload);
      else await API.put(`/promotions/${modal.id}`, payload);
      setModal(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await API.delete(`/promotions/${id}`); load();
  };

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-dark">Promotional</h1>
          <p className="text-sm text-gray-500 mt-1">News, announcements, and events shown on the public Promotional page</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm flex items-center gap-2">
          <PlusIcon className="w-4 h-4" /> Add Post
        </button>
      </div>

      <div className="bg-white border border-gray-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left">
              {['Image', 'Title', 'Category', 'Date', 'Status', ''].map(h => (
                <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No posts yet. Click "Add Post" to get started.</td></tr>
            )}
            {items.map(item => (
              <tr key={item.id}>
                <td className="px-4 py-3">
                  <div className="w-14 h-10 bg-gray-100 overflow-hidden">
                    {item.image_url
                      ? <img src={imgSrc(item.image_url)} alt={item.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-primary/10" />}
                  </div>
                </td>
                <td className="px-4 py-3 font-medium text-gray-800">{item.title}</td>
                <td className="px-4 py-3 text-gray-500 capitalize">{item.category}</td>
                <td className="px-4 py-3 text-gray-500">{formatDate(item.published_at)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {item.is_active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-primary border border-gray-200 hover:border-primary transition-colors">
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'add' ? 'Add Post' : 'Edit Post'} onClose={() => setModal(null)} wide>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Title *</label>
              <input value={form.title} onChange={e => handleTitleChange(e.target.value)}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Slug *</label>
              <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: sanitizeSlugInput(e.target.value) }))}
                className="w-full border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Publish Date</label>
                <input type="date" value={form.published_at || ''} onChange={e => setForm(f => ({ ...f, published_at: e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Excerpt</label>
              <input value={form.excerpt || ''} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                placeholder="Short one-line summary shown on the list" className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Content</label>
              <RichTextEditor value={form.content} onChange={html => setForm(f => ({ ...f, content: html }))} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cover Image</label>
              {form.image_url && (
                <div className="h-32 bg-gray-50 border border-gray-200 overflow-hidden mb-2">
                  <img src={imgSrc(form.image_url)} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer mb-2" />
              <input value={form.image_url || ''} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                placeholder="or paste image URL..." className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              <p className="text-xs text-gray-400 mt-1">Shown on the list/featured card and at the top of the post's page. To add a video, use the video button inside the Content editor above instead.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value={1}>Active</option>
                  <option value={0}>Hidden</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || !form.title || !form.slug} className="btn-primary text-sm disabled:opacity-60 flex-1">
                {saving ? 'Saving...' : 'Save Post'}
              </button>
              <button onClick={() => setModal(null)} className="btn-outline text-sm flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
